import React, { useCallback, useEffect, useRef } from 'react';
import type { VirtuosoHandle } from 'react-virtuoso';
import { useEnvironment } from '@wix/yoshi-flow-editor';

interface IProps {
  size: number;
}

export function useKeyboardScroll(props: IProps) {
  const { size } = props;
  const { isSSR, isMobile, isEditor } = useEnvironment();

  const $virtuoso = useRef<VirtuosoHandle>(null);

  const [active, setActive] = React.useState(-1);
  const [scrolledTo, setScrolledTo] = React.useState(-1);

  const keyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      let next: number = -1;
      const key: number = event.which || event.keyCode;

      // if focus is not inside feed - do nothing
      if (!(event.target as Element)?.closest('[role="article"]')) {
        return;
      }

      switch (key) {
        // PageUp
        case 33:
          next = Math.max(0, active - 1);
          break;

        // PageDown
        case 34:
          next = Math.min(size, active + 1);
          break;

        // Home
        case 36:
          next = 0;
          break;

        // End
        case 35:
          next = size - 1;
          break;

        default:
          return;
      }

      if (next !== -1) {
        event.preventDefault();
        $virtuoso.current?.scrollIntoView({
          index: next,
          align: 'center',
          behavior: 'smooth',
          done: () => setScrolledTo(next),
        });
      }
    },
    [active, size],
  );

  useEffect(() => {
    if (isMobile || isEditor || isSSR) {
      return;
    }

    window.addEventListener('keydown', keyDownHandler);

    return function () {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [keyDownHandler]);

  return {
    $virtuoso,
    scrolledTo,
    setActive,
  };
}
